import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCartContainer } from '@components/organisms/ShoppingCartContainer';
import { IconAccordion } from '@components/molecules/IconAccordion';
import { AddressOption } from '@components/molecules/AddressOption';
import { RadioButton, RadioButtonType } from '@components/atoms/RadioButton';
import { SelectableAddress } from '@types';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useAddDeliveryMethodToCart } from '@services/useAddDeliveryMethodToCart';
import { useData } from '@hooks/useData';
import { useGetAllNearbyPickupSpots } from '@services/useGetAllNearbyPickupSpots';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { useGetPickupSpotDetail } from '@services/useGetPickupSpotDetail';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType } from '@components/atoms/Select';
import { useGetAllShippingCountries } from '@services/useGetAllShippingCountries';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Notification } from '@components/molecules/Notification';
import { AccordionRef } from '@components/atoms/Accordion';
import { NewAddressForm } from '@components/molecules/NewAddressForm';
import useCartSummary from '@hooks/useCartSummary';

const CURRENT_STEP = 3;
const RADIO_TYPE = RadioButtonType.normal;

export const DeliveryAddress: FC = () => {
    const { navigate } = useCustomNavigate();
    const { login, allSettings, cart, addresses, allGeoDistricts } = useData();
    const { request: requestAddDeliveryMethodToCart, lastResponseTime } = useAddDeliveryMethodToCart();
    const { formattedCart } = useCartSummary(cart?.data);
    const { t } = useTranslation();

    const translations = {
        title: t('delivery-address-component.title'),
        addressTitle: t('delivery-address-component.address.title'),
        addressDescription: t('delivery-address-component.address.description'),
        storeTitle: t('delivery-address-component.store.title'),
        storeDescription: t('delivery-address-component.store.description'),
        pickupSpotTitle: t('delivery-address-component.pickup-spot.title'),
        pickupSpotDescription: t('delivery-address-component.pickup-spot.description'),
        continueButton: t('delivery-address-component.continue.button'),
        requiredMinOrderPrice: t('shopping-cart-general-messages.required-min-order-price', {
            price: cart?.data?.paymentRequirements?.minOrderPrice,
        }),
    };

    useEffect(() => {
        translations.requiredMinOrderPrice = t('shopping-cart-general-messages.required-min-order-price', {
            price: cart?.data?.paymentRequirements?.minOrderPrice,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.data?.paymentRequirements?.minOrderPrice]);

    const canDeliverToHomeAddress = allSettings?.data?.shippingMethod === 'CTT';
    const canDeliverToPickupSpot = allSettings?.data?.shippingMethod === 'DPD';

    const deliveryAddresses =
        (addresses?.data || [])
            .filter((item) => item.type === 'delivery')
            .map((item, key) => {
                const matchingDistrict = Object.values(allGeoDistricts?.data || {}).find(
                    (district) => district.district_code === item.district
                );

                return {
                    value: 'address-' + key,
                    uuid: item.uuid || '',
                    name: item.name || '',
                    lastName: item.lastName || '',
                    fiscalNumber: item.fiscalNumber || '',
                    address: `${item.address || ''} ${item.complement || ''}`.trim(),
                    postalCode: `${item.postalCode || ''} ${matchingDistrict?.name || ''}`.trim(),
                    region: `${item.region || ''}`,
                    initiallySelected: !!item.primary,
                };
            }) ?? [];

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            navigate(`/${SITE_URLS.login}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    const accordionRef = useRef<AccordionRef>(null);

    // tab initially open
    const [initialOpenTab, setInitialOpenTab] = useState<'address' | 'at-store' | 'pickup-spot' | null>(null);

    useEffect(() => {
        if (allSettings?.data?.shippingMethod && initialOpenTab === null) {
            if (cart?.data?.delivery?.shipToPickupSpotId !== undefined && cart?.data?.delivery?.shipToPickupSpotId !== null) {
                setInitialOpenTab('pickup-spot');
            } else if (cart?.data?.delivery?.pickupAtStore) {
                setInitialOpenTab('at-store');
            } else if (cart?.data?.delivery?.shipToAddress) {
                setInitialOpenTab('address');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cart?.data?.delivery?.shipToAddress,
        cart?.data?.delivery?.pickupAtStore,
        cart?.data?.delivery?.shipToPickupSpotId,
        allSettings?.data?.shippingMethod,
    ]);

    useEffect(() => {
        if (initialOpenTab) {
            accordionRef.current?.open(initialOpenTab);
        }
    }, [initialOpenTab]);

    /* shipping to home address */

    const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
    const [addressType, setAddressType] = useState<'old' | 'new'>('old');

    const handleOnChangeAddress = (id: string) => setSelectedAddress(id);

    const [lastAddedAddress, setLastAddedAddress] = useState<SelectableAddress | null>(null);

    /* at store pickup */

    const [selectedAtStorePickup, setSelectedAtStorePickup] = useState<boolean>(false);

    const handleOnSelectAtStorePickup = () => setSelectedAtStorePickup(true);

    /* shipping to pickup spot */

    const [selectedPickupSpot, setSelectedPickupSpot] = useState<string | null>(null);

    const handleOnChangePickupSpot = (value: string | null) => setSelectedPickupSpot(value);

    // check if order has min price
    const [hasMinOrderPrice, setHasMinOrderPrice] = useState<false | null>(null);

    // may the user proceed to next page?
    const isButtonEnabled =
        // has address selected
        selectedAddress !== null ||
        // has at store pickup selected
        cart?.data?.delivery?.pickupAtStore ||
        selectedAtStorePickup ||
        // has pickup spot selected
        cart?.data?.delivery?.shipToPickupSpotId ||
        selectedPickupSpot !== null;

    useEffect(() => {
        // unselect other delivery types
        if ((addressType === 'old' && lastAddedAddress) || selectedAddress) {
            setSelectedAtStorePickup(false);
            setSelectedPickupSpot(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressType, selectedAddress]);

    // when user selects at store pickup
    useEffect(() => {
        // unselect other delivery types
        if (selectedAtStorePickup) {
            setSelectedAddress(null);
            setSelectedPickupSpot(null);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAtStorePickup]);

    // when user selects a pickup spot
    useEffect(() => {
        // unselect other delivery types
        if (selectedPickupSpot) {
            setSelectedAddress(null);
            setSelectedAtStorePickup(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPickupSpot]);

    useEffect(() => {
        if (addressType === 'new' && !lastAddedAddress) {
            const lastAddress = deliveryAddresses[deliveryAddresses.length - 1];
            if (lastAddress) {
                setLastAddedAddress(lastAddress);
                handleOnChangeAddress(lastAddress.value);
            }
            setAddressType('old');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryAddresses.length]);

    const handleSaveDelivery = () => {
        if (selectedAddress) {
            let selectedUUID: string | undefined = undefined;
            const selectedItem = deliveryAddresses.find((item) => item.value === selectedAddress);
            selectedUUID = selectedItem ? selectedItem.uuid : undefined;

            requestAddDeliveryMethodToCart({ address_uuid: selectedUUID });
        } else if (selectedAtStorePickup) {
            requestAddDeliveryMethodToCart({ at_store_pickup: true });
        } else if (selectedPickupSpot) {
            requestAddDeliveryMethodToCart({ pickup_spot_id: selectedPickupSpot || '' });
        } else {
            // changed nothing, go to next step
            navigate(`/${SITE_URLS.orderPayment}`);
        }
    };

    useEffect(() => {
        if (lastResponseTime) {
            if (!cart?.data?.paymentRequirements?.hasMinOrderPrice) {
                setHasMinOrderPrice(false);
            } else {
                navigate(`/${SITE_URLS.orderPayment}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const formattedTotal = formattedCart();

    return (
        <ShoppingCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Button
                        type="button"
                        label={translations.continueButton}
                        onClick={handleSaveDelivery}
                        mode={ButtonMode.primary}
                        disabled={!isButtonEnabled}
                    />
                </div>
            }
            total={formattedTotal}>
            <>
                {hasMinOrderPrice === false && (
                    <Notification
                        type="error"
                        message={translations.requiredMinOrderPrice}
                        borderRadiusStyle="rounded"
                        size="full-width"
                    />
                )}

                <IconAccordion
                    accordionRef={accordionRef}
                    id="shopping-accordion"
                    items={[
                        canDeliverToHomeAddress
                            ? {
                                  id: 'address',
                                  title: translations.addressTitle,
                                  description: translations.addressDescription,
                                  icon: 'fa-thin fa-house',
                                  content: (
                                      <AtAddress
                                          addresses={deliveryAddresses}
                                          selectedAddress={selectedAddress}
                                          lastAddedAddress={lastAddedAddress}
                                          handleOnChangeAddress={handleOnChangeAddress}
                                          addressType={addressType}
                                          setAddressType={setAddressType}
                                      />
                                  ),
                              }
                            : undefined,
                        {
                            id: 'at-store',
                            title: translations.storeTitle,
                            description: translations.storeDescription,
                            icon: 'fa-thin fa-store',
                            content: (
                                <AtStorePickup
                                    selectedAtStorePickup={selectedAtStorePickup}
                                    handleOnSelectAtStorePickup={handleOnSelectAtStorePickup}
                                />
                            ),
                        },
                        canDeliverToPickupSpot
                            ? {
                                  id: 'pickup-spot',
                                  title: translations.pickupSpotTitle,
                                  description: translations.pickupSpotDescription,
                                  icon: 'fa-thin fa-location-dot',
                                  content: (
                                      <PickupSpotsForm
                                          selectedPickupSpot={selectedPickupSpot}
                                          handleOnChangePickupSpot={handleOnChangePickupSpot}
                                          selectedAtStore={selectedAtStorePickup}
                                          selectedAddress={selectedAddress !== null}
                                      />
                                  ),
                              }
                            : undefined,
                    ].filter((item) => item !== undefined)}
                />
            </>
        </ShoppingCartContainer>
    );
};

export interface AtAddressProps {
    addresses: SelectableAddress[];
    selectedAddress: string | null;
    lastAddedAddress: SelectableAddress | null;
    handleOnChangeAddress: (id: string) => void;
    addressType: 'old' | 'new';
    setAddressType: (value: 'old' | 'new') => void;
}

const AtAddress: FC<AtAddressProps> = ({
    addresses,
    selectedAddress,
    lastAddedAddress,
    handleOnChangeAddress,
    addressType,
    setAddressType,
}) => {
    const { cart } = useData();
    const { t } = useTranslation();

    const translations = {
        addNewAddress: t('delivery-address-component.address.add-new-address'),
    };

    useEffect(() => {
        const initiallySelectedAddress = addresses.find((item) => item.uuid === cart?.data?.delivery?.shipToAddress?.uuid);
        if (initiallySelectedAddress) {
            handleOnChangeAddress(initiallySelectedAddress.value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.data?.delivery?.shipToAddress]);

    return (
        <>
            <IconAccordion.IndentedContent>
                <div className="row pe-2 g-4">
                    {addresses.map((address, index) => (
                        <div key={`address-${index}`} className="col-12 col-xl-6">
                            <AddressOption
                                radioButton={{
                                    id: `existing-address-${index}`,
                                    name: 'existing-address',
                                    value: address.value,
                                    type: RADIO_TYPE,
                                    checked: address.value === selectedAddress || lastAddedAddress?.value === address.value,
                                    onChange: handleOnChangeAddress,
                                }}
                                name={address.name}
                                lastName={address.lastName}
                                fiscalNumber={address.fiscalNumber}
                                address={address.address}
                                postalCode={address.postalCode}
                                region={address.region}
                            />
                        </div>
                    ))}
                </div>
            </IconAccordion.IndentedContent>
            <hr className="my-0" />
            <IconAccordion.IndentedContent>
                <Button mode={ButtonMode.link} label={translations.addNewAddress} onClick={() => setAddressType('new')} />
            </IconAccordion.IndentedContent>
            {addressType === 'new' ? <NewAddressForm type={'delivery'} /> : ''}
        </>
    );
};

export interface AtStorePickupProps {
    selectedAtStorePickup: boolean;
    handleOnSelectAtStorePickup: () => void;
}

const AtStorePickup: FC<AtStorePickupProps> = ({ selectedAtStorePickup, handleOnSelectAtStorePickup }) => {
    const { cart } = useData();
    const { t } = useTranslation();

    const translations = {
        description: t('delivery-address-component.store.explain'),
    };

    useEffect(() => {
        if (cart?.data?.delivery?.pickupAtStore === true) {
            handleOnSelectAtStorePickup();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.data?.delivery?.pickupAtStore]);

    return (
        <IconAccordion.IndentedContent>
            <div className="d-flex align-items-center mb-4">
                <div className="me-4">
                    <RadioButton
                        id="at-store-pickup"
                        name="at-store-pickup"
                        value="at-store-pickup"
                        type={RadioButtonType.normal}
                        checked={selectedAtStorePickup}
                        onChange={(value) => value === 'at-store-pickup' && handleOnSelectAtStorePickup()}
                    />
                </div>
                <div>
                    <Typography classes="mb-1" fontSize="14">
                        {translations.description}
                    </Typography>
                </div>
            </div>
            {/* list of all the stores where the packages will be */}
            <div className="row">
                {cart?.data?.merchantsAddresses?.map((merchant, index) => (
                    <div key={`at-store-pickup-${index}`} className="col-md-6 col-12 mb-3">
                        <Typography classes="mb-1" fontWeight="900">
                            {merchant.commercial_name || ''}
                        </Typography>
                        <Typography classes="mb-1" fontSize="14">
                            {merchant.street}
                        </Typography>
                        <Typography classes="mb-0" fontSize="14">
                            {`${merchant.postal_code} ${merchant.city}`}
                        </Typography>
                    </div>
                ))}
            </div>
        </IconAccordion.IndentedContent>
    );
};

export interface PickupSpotsFormProps {
    selectedPickupSpot: string | null;
    handleOnChangePickupSpot: (value: string | null) => void;
    selectedAddress: boolean;
    selectedAtStore: boolean;
}

interface PickupSpotsFormErrors {
    country: string[];
}

const PickupSpotsForm: FC<PickupSpotsFormProps> = ({
    selectedPickupSpot,
    handleOnChangePickupSpot,
    selectedAddress,
    selectedAtStore,
}) => {
    const { t } = useTranslation();
    const { cart, allShippingCountries, pickupSpotDetail, allNearbyPickupSpots, setAllNearbyPickupSpots } = useData();
    const {
        request: requestGetPickupSpotDetail,
        loading: loadingGetPickupSpotDetail,
        error: errorGetPickupSpotDetail,
    } = useGetPickupSpotDetail();
    const {
        request: requestGetAllNearbyPickupSpots,
        loading: loadingGetAllNearbyPickupSpots,
        error: errorGetAllNearbyPickupSpots,
        endpointInput: inputGetAllNearbyPickupSpots,
    } = useGetAllNearbyPickupSpots();
    const {
        request: requestGetAllShippingCountries,
        loading: loadingGetAllShippingCountries,
        error: errorGetAllShippingCountries,
    } = useGetAllShippingCountries();

    const translations = {
        countryLabel: t('pickups-spots-form-component.country.label'),
        countryRequiredError: t('pickups-spots-form-component.country.required-error'),
        selectCountryDefault: t('select-default-option'),
        postalCodeLabel: t('pickups-spots-form-component.postal-code.label'),
    };

    const [pickupCountry, setPickupCountry] = useState<string>('');
    const [pickupPostalCode, setPickupPostalCode] = useState<string>('');

    // errors to be displayed in the form
    const [errors, setErrors] = useState<PickupSpotsFormErrors>({
        country: [],
    });

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this accordion
        return () => {
            setAllNearbyPickupSpots(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // call services, if we don't have the information yet
    useEffect(() => {
        !allShippingCountries && !loadingGetAllShippingCountries && requestGetAllShippingCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allShippingCountries]);

    const validateForm: () => PickupSpotsFormErrors = () => {
        const newErrors: PickupSpotsFormErrors = {
            country: [],
        };

        pickupCountry === '' && pickupPostalCode.trim() !== '' && newErrors.country.push(translations.countryRequiredError);

        setErrors(newErrors);

        return newErrors;
    };

    const searchNearbyPickupSpots = () => {
        // must have types at least 2 characters to call service
        if (pickupPostalCode && pickupPostalCode.length >= 2) {
            const errors = validateForm();

            if (errors.country.length === 0) {
                // prevent multiple instances of service being called
                !loadingGetAllNearbyPickupSpots &&
                    requestGetAllNearbyPickupSpots({ country_code: pickupCountry, zip_code: pickupPostalCode });
            }
        } else {
            setAllNearbyPickupSpots(null);
        }
    };

    useEffect(() => {
        searchNearbyPickupSpots();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickupPostalCode, pickupCountry]);

    // verification to make sure that the result/error matches correctly the postal code being searched
    // when GetAllNearbyPickupSpots endpoints is no longer loading
    // call service one last time
    useEffect(() => {
        if (
            !loadingGetAllNearbyPickupSpots &&
            (inputGetAllNearbyPickupSpots?.country_code !== pickupCountry ||
                inputGetAllNearbyPickupSpots?.zip_code !== pickupPostalCode)
        ) {
            searchNearbyPickupSpots();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingGetAllNearbyPickupSpots]);

    useEffect(() => {
        // if user unselected the country, reset results
        if (pickupCountry === '') {
            setAllNearbyPickupSpots(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickupCountry]);

    // get information about pickup spot
    useEffect(() => {
        if (cart?.data?.delivery?.shipToPickupSpotId) {
            requestGetPickupSpotDetail({ id: cart?.data?.delivery?.shipToPickupSpotId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.data?.delivery?.shipToPickupSpotId]);

    const handleOnChangePostalCode = (value: string) => {
        setPickupPostalCode(value);
        // reset selection
        handleOnChangePickupSpot(null);
    };

    const countriesOptionsData: { name?: string; iso_code?: string }[] = allShippingCountries?.data || [];

    const countriesAllOptions: { text: string; value: string }[] = [
        { text: translations.selectCountryDefault, value: '' },
        ...countriesOptionsData.map((country) => ({
            text: country.name || '',
            value: country.iso_code || '',
        })),
    ];

    return (
        <IconAccordion.IndentedContent>
            <div className="pb-4">
                <div className="row">
                    <div className="col-md-6 col-12">
                        <Typography color="muted" fontSize="12">
                            {translations.countryLabel}
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            options={countriesAllOptions}
                            value={pickupCountry || undefined}
                            onChange={(value) => setPickupCountry(value)}
                        />
                        {errors.country.length > 0 && <ErrorMessages messages={errors.country} />}
                    </div>
                    <div className="col-md-6 col-12 pt-2 mt-1">
                        <Input
                            type={InputTypes.text}
                            label={`${translations.postalCodeLabel}*`}
                            variant={InputVariants.labeledInput}
                            value={pickupPostalCode}
                            onChange={(value) => handleOnChangePostalCode(value)}
                            maxLength={8}
                        />
                    </div>
                    <div className="col-12 pt-2">
                        {errorGetAllShippingCountries && <ErrorMessages messages={[errorGetAllShippingCountries]} />}
                        {errorGetAllNearbyPickupSpots && <ErrorMessages messages={[errorGetAllNearbyPickupSpots]} />}
                    </div>
                </div>

                {/* display selected pickup spot */}
                {!selectedPickupSpot &&
                    !selectedAddress &&
                    !selectedAtStore &&
                    !errorGetPickupSpotDetail &&
                    !loadingGetPickupSpotDetail &&
                    cart?.data?.delivery?.shipToPickupSpotId &&
                    pickupSpotDetail?.data && (
                        <div className="pt-4">
                            <AddressOption
                                radioButton={{
                                    id: 'selected-pickup',
                                    name: 'selected-pickup',
                                    value: cart?.data?.delivery?.shipToPickupSpotId || '',
                                    type: RADIO_TYPE,
                                    checked: true,
                                }}
                                name={pickupSpotDetail?.data?.name || ''}
                                address={
                                    pickupSpotDetail?.data?.addressLine1 +
                                    ' ' +
                                    pickupSpotDetail?.data?.addressLine2 +
                                    ' ' +
                                    pickupSpotDetail?.data?.streetNumber
                                }
                                postalCode={`${pickupSpotDetail?.data?.postalCode} ${pickupSpotDetail?.data?.city}`}
                            />
                        </div>
                    )}
                {errorGetPickupSpotDetail && <ErrorMessages messages={[errorGetPickupSpotDetail]} />}
            </div>

            {/* list of search results for pickup spots */}
            <div className="row pe-2 g-4">
                {!errorGetAllNearbyPickupSpots &&
                    !loadingGetAllNearbyPickupSpots &&
                    allNearbyPickupSpots?.data?.map((pickupSpot, index) => (
                        <div key={`pickup-${index}`} className="col-12 col-xl-6">
                            <AddressOption
                                radioButton={{
                                    id: `pickup-${index}`,
                                    name: 'pickup',
                                    value: pickupSpot.id || '',
                                    type: RADIO_TYPE,
                                    checked: pickupSpot.id === selectedPickupSpot,
                                    onChange: handleOnChangePickupSpot,
                                }}
                                name={pickupSpot.name || ''}
                                address={pickupSpot.addressLine1 + ' ' + pickupSpot.addressLine2 + ' ' + pickupSpot.streetNumber}
                                postalCode={`${pickupSpot.postalCode} ${pickupSpot.city}`}
                            />
                        </div>
                    ))}
            </div>
        </IconAccordion.IndentedContent>
    );
};
